import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, Icon } from '../components/common'
import { useImages, useImageOperations } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { deleteFile, extractPublicIdFromUrl } from '../services/storage'
import './ImagesView.css'

// LocalStorage key for tier filter persistence
const TIER_FILTER_KEY = 'tierFilters_images'

/**
 * Helper to get image URL from either string or object format
 * Supports both legacy (string) and new ({ url, publicId }) formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || image.storageUrl || ''
}

/**
 * Get entity type icon
 */
const getEntityIconName = (type) => {
  switch (type) {
    case 'character': return 'node-diamond'
    case 'location': return 'locations'
    case 'event': return 'node-circle'
    case 'shot': return 'shots'
    default: return 'node-circle'
  }
}

/**
 * Get entity type label
 */
const getEntityLabel = (type) => {
  switch (type) {
    case 'character': return 'Character'
    case 'location': return 'Location'
    case 'event': return 'Event'
    case 'shot': return 'Shot'
    default: return type || 'Unknown'
  }
}

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Card animation variants
const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95 
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.05,
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  })
}

// Modal variants
const modalVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.9 
  },
  visible: { 
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 25
    }
  },
  exit: { 
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }
}

/**
 * ImageDetailModal - Modal for viewing and editing image metadata
 */
function ImageDetailModal({ image, onClose, onSave, onDelete, canEdit = true, canDelete = true }) {
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    prompt: image?.prompt || '',
    model: image?.model || '',
    notes: image?.notes || '',
  })
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!image) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onSave(image.id, formData)
      setIsEditing(false)
    } catch (err) {
      console.error('Failed to save image:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this image? This action cannot be undone.')) return
    
    setDeleting(true)
    try {
      await onDelete(image)
    } catch (err) {
      console.error('Failed to delete image:', err)
    } finally {
      setDeleting(false)
    }
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <motion.div 
      className="image-modal"
      initial="hidden"
      animate="visible"
      exit="exit"
      onClick={handleBackdropClick}
    >
      <motion.div 
        className="image-modal__backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />
      
      <motion.div 
        className="image-modal__content"
        variants={modalVariants}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="image-modal__close" onClick={onClose}>
          <Icon name="close" size={20} />
        </button>
        
        <div className="image-modal__image-section">
          <img 
            src={getImageUrl(image.storageUrl || image.url || image)} 
            alt={image.filename || 'Image'}
            className="image-modal__image"
          />
        </div>
        
        <div className="image-modal__details">
          <div className="image-modal__header">
            <h2 className="image-modal__title">
              {image.filename || 'Untitled Image'}
            </h2>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit}>
              <div className="image-modal__field">
                <label className="image-modal__label">Prompt</label>
                <textarea
                  className="image-modal__textarea"
                  value={formData.prompt}
                  onChange={(e) => setFormData({ ...formData, prompt: e.target.value })}
                  placeholder="Describe the image prompt..."
                  rows={4}
                />
              </div>

              <div className="image-modal__field">
                <label className="image-modal__label">Model</label>
                <input
                  type="text"
                  className="image-modal__input"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="e.g., DALL-E 3, Midjourney v6"
                />
              </div>

              <div className="image-modal__field">
                <label className="image-modal__label">Notes</label>
                <textarea
                  className="image-modal__textarea"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>

              <div className="image-modal__actions">
                <button 
                  type="button"
                  className="image-modal__btn image-modal__btn--cancel"
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="image-modal__btn image-modal__btn--save"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          ) : (
            <>
              {image.prompt && (
                <div className="image-modal__field">
                  <span className="image-modal__label">Prompt</span>
                  <p className="image-modal__value">{image.prompt}</p>
                </div>
              )}

              {image.model && (
                <div className="image-modal__field">
                  <span className="image-modal__label">Model</span>
                  <p className="image-modal__value">{image.model}</p>
                </div>
              )}

              {image.notes && (
                <div className="image-modal__field">
                  <span className="image-modal__label">Notes</span>
                  <p className="image-modal__value">{image.notes}</p>
                </div>
              )}

              {image.linkedEntityType && (
                <div className="image-modal__field">
                  <span className="image-modal__label">Linked Entity</span>
                  <div className="image-modal__entity-link">
                    <Icon name={getEntityIconName(image.linkedEntityType)} size={16} />
                    <span>{getEntityLabel(image.linkedEntityType)}</span>
                  </div>
                </div>
              )}

              {image.createdAt && (
                <div className="image-modal__field">
                  <span className="image-modal__label">Created</span>
                  <p className="image-modal__value">
                    {image.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                  </p>
                </div>
              )}

              <div className="image-modal__actions">
                {canDelete && (
                  <button 
                    className="image-modal__btn image-modal__btn--delete"
                    onClick={handleDelete}
                    disabled={deleting}
                    title="Delete image"
                  >
                    {deleting ? '...' : <Icon name="trash" size={16} />}
                  </button>
                )}
                <button 
                  className="image-modal__btn image-modal__btn--cancel"
                  onClick={onClose}
                >
                  Close
                </button>
                {canEdit && (
                  <button 
                    className="image-modal__btn image-modal__btn--save"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

/**
 * ImagesView - Gallery view for all uploaded images
 */
export default function ImagesView() {
  // Filter state
  const [entityFilter, setEntityFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedImage, setSelectedImage] = useState(null)
  
  // Tier filter state (default: A and B shown, C hidden)
  const [tierFilters, setTierFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(TIER_FILTER_KEY)
      return saved ? JSON.parse(saved) : { A: true, B: true, C: false }
    } catch {
      return { A: true, B: true, C: false }
    }
  })

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canEdit = can('edit')
  const canDelete = can('delete')

  // Firestore data and operations (project-scoped)
  const { data: images, loading, error } = useImages(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
  const imageOps = useImageOperations(projectId)

  // Toggle a tier filter
  const toggleTier = (tier) => {
    const newFilters = { ...tierFilters, [tier]: !tierFilters[tier] }
    setTierFilters(newFilters)
    try {
      localStorage.setItem(TIER_FILTER_KEY, JSON.stringify(newFilters))
    } catch {
      // Ignore localStorage errors
    }
  }

  // Filter and search images
  const filteredImages = useMemo(() => {
    if (!images) return []
    
    return images.filter(image => {
      // Entity type filter
      if (entityFilter !== 'all' && image.linkedEntityType !== entityFilter) {
        return false
      }
      
      // Tier filter
      const tier = (image.tier || 'B').toUpperCase()
      if (tierFilters[tier] === false) {
        return false
      }
      
      // Search filter (search in prompt, notes, filename)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        const searchFields = [
          image.prompt,
          image.notes,
          image.filename,
          image.model,
        ].filter(Boolean).map(s => s.toLowerCase())
        
        return searchFields.some(field => field.includes(query))
      }
      
      return true
    })
  }, [images, entityFilter, tierFilters, searchQuery])

  // Handle save
  const handleSave = async (imageId, data) => {
    try {
      await imageOps.update(imageId, data)
      setSelectedImage(prev => prev ? { ...prev, ...data } : null)
    } catch (err) {
      console.error('Failed to update image:', err)
      throw err
    }
  }

  // Handle delete
  const handleDelete = async (image) => {
    try {
      // Delete from Cloudinary
      if (image.storageUrl) {
        const publicId = image.publicId || extractPublicIdFromUrl(image.storageUrl)
        if (publicId) {
          await deleteFile(publicId)
        }
      }
      
      // Delete from Firestore
      await imageOps.remove(image.id)
      setSelectedImage(null)
    } catch (err) {
      console.error('Failed to delete image:', err)
      throw err
    }
  }

  // Truncate text helper
  const truncate = (text, length = 60) => {
    if (!text) return ''
    return text.length > length ? text.slice(0, length) + '...' : text
  }

  return (
    <motion.div 
      className="images-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="images-view__header">
        <div className="images-view__title-group">
          <span className="images-view__icon"><Icon name="images" size={28} /></span>
          <h1 className="images-view__title">Images</h1>
        </div>
        <p className="images-view__subtitle">
          {loading ? 'Loading...' : `${filteredImages.length} image${filteredImages.length !== 1 ? 's' : ''}`}
          {entityFilter !== 'all' && ` in ${getEntityLabel(entityFilter)}s`}
          {searchQuery && ` matching "${searchQuery}"`}
        </p>
      </header>

      {/* Filter Bar */}
      <div className="images-view__filters">
        <div className="images-view__filter-group">
          <label className="images-view__filter-label">Type:</label>
          <select 
            className="images-view__filter-select"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="character">Characters</option>
            <option value="location">Locations</option>
            <option value="event">Events</option>
            <option value="shot">Shots</option>
          </select>
        </div>

        <div className="images-view__tier-filters">
          <span className="images-view__filter-label">Tier:</span>
          {['A', 'B', 'C'].map(tier => (
            <button
              key={tier}
              className={`images-view__tier-btn images-view__tier-btn--${tier.toLowerCase()} ${tierFilters[tier] ? 'images-view__tier-btn--active' : ''}`}
              onClick={() => toggleTier(tier)}
              title={`${tierFilters[tier] ? 'Hide' : 'Show'} Tier ${tier} images`}
            >
              {tier}
            </button>
          ))}
        </div>
        
        <div className="images-view__search">
          <span className="images-view__search-icon"><Icon name="search" size={16} /></span>
          <input
            type="text"
            className="images-view__search-input"
            placeholder="Search by prompt, model, or filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="images-view__error">
          <h3>Error loading images</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="images-view__loading">
          <div className="images-view__spinner" />
          <p>Loading images...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && images?.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="images-view__empty">
          <div className="images-view__empty-icon"><Icon name="images" size={48} /></div>
          <h2>No images yet</h2>
          <p>Images uploaded to characters, locations, events, and shots will appear here.</p>
        </GlassPanel>
      )}

      {/* No Results State */}
      {!loading && !error && images?.length > 0 && filteredImages.length === 0 && (
        <div className="images-view__no-results">
          <div className="images-view__no-results-icon"><Icon name="search" size={48} /></div>
          <h3>No matching images</h3>
          <p>Try adjusting your filters or search query.</p>
        </div>
      )}

      {/* Image Grid */}
      {!loading && !error && filteredImages.length > 0 && (
        <div className="images-view__grid">
          {filteredImages.map((image, index) => (
            <motion.div
              key={image.id}
              className="images-view__grid-item"
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <div 
                className="images-view__card"
                onClick={() => setSelectedImage(image)}
              >
                <div className="images-view__card-image-wrapper">
                  <img
                    src={getImageUrl(image.storageUrl || image.url)}
                    alt={image.filename || 'Image'}
                    className="images-view__card-image"
                    loading="lazy"
                  />
                  <div className="images-view__card-overlay" />
                  
                  <div className="images-view__card-badges">
                    {image.linkedEntityType && (
                      <span className="images-view__badge images-view__badge--entity">
                        <Icon name={getEntityIconName(image.linkedEntityType)} size={12} /> {image.linkedEntityType}
                      </span>
                    )}
                    {image.model && (
                      <span className="images-view__badge images-view__badge--model">
                        {image.model}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="images-view__card-info">
                  {image.prompt && (
                    <p className="images-view__card-prompt">
                      {truncate(image.prompt)}
                    </p>
                  )}
                  {!image.prompt && image.filename && (
                    <p className="images-view__card-prompt">
                      {image.filename}
                    </p>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Image Detail Modal */}
      <AnimatePresence>
        {selectedImage && (
          <ImageDetailModal
            image={selectedImage}
            onClose={() => setSelectedImage(null)}
            onSave={handleSave}
            onDelete={handleDelete}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}

